import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getTransactionSummary, getBankStatusApi, getApplicationMessageApi } from "../api/api";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appMessage, setAppMessage] = useState<string>("");

  const [bankStatusList, setBankStatusList] = useState<any[]>([]);
  const [bankError, setBankError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const doFetch = async () => {
      if (!user || !user.token) return;
      setError(null);
      setLoading(true);
      try {
        const payload = { login_id: (user as any)?.id ?? 5 };
        const body = await getTransactionSummary(payload, user.token);
        if (!mounted) return;
        if (!body?.success) {
          setError(body?.message || "Failed to fetch summary");
          setSummary(null);
        } else {
          setSummary(body.data);
        }
      } catch (err: any) {
        if (!mounted) return;
        setError(err.message || "Request failed");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    const fetchBankStatus = async () => {
      if (!user || !user.token) return;
      try {
        const payload = { login_id: (user as any)?.id ?? 5 };
        const body = await getBankStatusApi(payload, user.token);
        if (!mounted) return;
        if (!body?.success) {
          setBankError(body?.message || "Failed to fetch bank status");
          setBankStatusList([]);
        } else {
          setBankStatusList(body.data || []);
        }
      } catch (err: any) {
        if (!mounted) return;
        setBankError(err.message || "Request failed");
      }
    };

    const fetchApplicationMessage = async () => {
      if (!user || !user.token) return;
      try {
        const body = await getApplicationMessageApi(user.token);
        if (!mounted) return;

        const message =
          (typeof body?.data?.message === "string" && body.data.message) ||
          (typeof body?.message === "string" && body.message) ||
          (typeof body?.data === "string" && body.data) ||
          "";

        setAppMessage(message);
      } catch (err: any) {
        if (!mounted) return;
        setAppMessage("");
      }
    };

    doFetch();
    fetchBankStatus();
    fetchApplicationMessage();

    const handler = () => {
      doFetch();
      fetchBankStatus();
      fetchApplicationMessage();
    };

    window.addEventListener("walletUpdated", handler);
    return () => {
      mounted = false;
      window.removeEventListener("walletUpdated", handler);
    };
  }, [user?.token]);

  const formatINR = (value: number | null) => {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(value);
  };

  const recentTransactions = summary?.recent_transactions ?? [];
  const userRole = (user?.role || (user as any)?.user_type || "").toString().toLowerCase();
  const isRetailer = userRole === "retailer";
  const formattedAppMessage = appMessage ? appMessage.replace(/,/g, ",\n") : "";
  const marqueeBanks =
    bankStatusList.length > 0
      ? bankStatusList
      : [{ bank_name: "Bank Status", bank_status: 1, remark: bankError || "Status updates unavailable" }];

  return (
    <div>
      <div className="page-header dashboard-page-header" style={{ display: "block", marginBottom: 20 }}>
        <h1 style={{ display: "block", margin: 0, fontSize: 28, fontWeight: 700 }}>Dashboard</h1>
        <p style={{ display: "block", marginTop: 6, color: "#6b7280" }}>Welcome to Money Transfer Portal</p>
      </div>

      {formattedAppMessage ? (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            borderRadius: 10,
            background: "#fef3c7",
            border: "1px solid #fde68a",
            color: "#92400e",
            fontWeight: 600,
            whiteSpace: "pre-line",
            lineHeight: 1.6,
            width: "40%",
            minWidth: 260,
          }}
        >
          {formattedAppMessage}
        </div>
      ) : null}

      {/* Bank Status Marquee */}
      {marqueeBanks.length > 0 && (
        <div className="bank-marquee-wrapper" style={{ overflow: "hidden" }}>
          <div className="bank-marquee-content">
            <div className="bank-marquee-group">
              {marqueeBanks.map((bank: any, index: number) => (
                <span key={`group1-${bank.bank_id ?? index}`} className="bank-marquee-item">
                  {bank.bank_status === "1" || bank.bank_status === 1 ? (
                    <span style={{ color: "green", fontWeight: 600 }}>
                      ✅ <span className="bank-name-blink">{bank.bank_name}</span>: {bank.remark || "is open now, please transfer money"}
                    </span>
                  ) : (
                    <span style={{ color: "red", fontWeight: 600 }}>
                      ❌ <span className="bank-name-blink">{bank.bank_name}</span>: {bank.remark || "is closed"}
                    </span>
                  )}
                </span>
              ))}
            </div>
            <div className="bank-marquee-group" aria-hidden="true">
              {marqueeBanks.map((bank: any, index: number) => (
                <span key={`group2-${bank.bank_id ?? index}`} className="bank-marquee-item">
                  {bank.bank_status === "1" || bank.bank_status === 1 ? (
                    <span style={{ color: "green", fontWeight: 600 }}>
                      ✅ <span className="bank-name-blink">{bank.bank_name}</span>: {bank.remark || "is open now, please transfer money"}
                    </span>
                  ) : (
                    <span style={{ color: "red", fontWeight: 600 }}>
                      ❌ <span className="bank-name-blink">{bank.bank_name}</span>: {bank.remark || "is closed"}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {bankError && <p style={{ color: "red", marginTop: 8 }}>{bankError}</p>}

      {isRetailer && (
        <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate("/send-money")}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Send Money
          </button>
          <button
            type="button"
            onClick={() => navigate("/retailer-transactionlog")}
            style={{
              background: "#0f766e",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Transaction Log
          </button>
          <button
            type="button"
            onClick={() => navigate("/history")}
            style={{
              background: "#374151",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Transaction History
          </button>
        </div>
      )}

      {/* Balance Summary Cards - image jaise */}

      {/* Balance Summary Cards 
      <div className="card">
        ...
      </div>
      */}
    </div>
  );
}